import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Table, Popconfirm, Input, Select, Modal } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { addUserLoad, userDeleteLoad } from "../../redux/slices/user.slice";
import images from "../../themes/appImage";
import {
  editOrgUserLoad,
  orgUserExportListLoad,
  orgUserListLoad,
} from "../../redux/slices/org.slice";
import { emptySpaceInputHandler } from "../../utils";
import {
  capitalizeFirstLetters,
  debounce,
  getFileType,
  setModalFun,
} from "../../utils/global-func";
import { departmentsListLoad } from "../../redux/slices/department.slice";
import { useFormik } from "formik";
import * as Yup from "yup";

const { Search } = Input;

const payload = {
  offset: 0,
  limit: 10,
  search: "",
  sort: "createdAt",
  order: -1,
};

let LIST_LIMIT = 1000;
let OFFSET = 0;
const numberOfRowOptionList = [
  { value: "one", label: 10 },
  { value: "two", label: 20 },
  { value: "three", label: 25 },
];

const UserTable = ({ myid }) => {
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();
  const [modal2Open, setModal2Open] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [record, setRecord] = useState(null);

  const { isLoading, addUserSuccess, userDeleteSuccess } = useSelector(
    (state) => state.userReducer
  );
  const { userList } = useSelector((state) => state.orgReducer);
  const [search, setSearch] = useState("");

  const { departmentListData, departmentsListPaginationData } = useSelector(
    (state) => state.departmentsReducer
  );
  const [department, setDepartment] = useState(null);
  let departMentOptions =
    departmentListData?.map((item) => ({
      label: capitalizeFirstLetters(item.name),
      value: item._id,
    })) || [];
  departMentOptions =
    departMentOptions?.length > 0
      ? [{ label: "All", value: null }, ...departMentOptions]
      : [{ label: "All", value: null }];

  useEffect(() => {
    const data = {
      offset: 0,
      limit: LIST_LIMIT,
      search: search,
      orgId: myid,
    };
    dispatch(departmentsListLoad(data));
  }, []);

  useEffect(() => {
    if (addUserSuccess) {
      setModal2Open(false);
    }
  }, [addUserSuccess]);

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append("csv", file);
      formData.append("organisation", myid);
      formData.append("type", getFileType(file.type));
      dispatch(addUserLoad(formData));
      fileInputRef.current.value = null;
    }
  };

  const formik = useFormik({
    initialValues: {
      department: record?.department?._id || null,
    },
    onSubmit: (values) => {
      dispatch(
        editOrgUserLoad({
          user: { id: values.department, user: record?._id },
          payload: { ...payload, organisation: myid },
        })
      );
    },
    validationSchema: Yup.object().shape({
      department: Yup.string()
        .nullable()
        .required("Please select a department."),
    }),
    enableReinitialize: true,
  });

  useEffect(() => {
    if (userDeleteSuccess || addUserSuccess) {
      dispatch(orgUserListLoad({ ...payload, organisation: myid }));
    }
  }, [userDeleteSuccess, addUserSuccess]);

  const handleDelete = (key) => {
    dispatch(userDeleteLoad({ ...payload, key }));
  };

  useEffect(() => {
    dispatch(orgUserListLoad({ ...payload, organisation: myid, department }));
  }, [dispatch, department]);

  const onChange = (pagination, filters, sorter, extra) => {
    const activePageNumber = pagination.current;
    payload.offset =
      activePageNumber * pagination.pageSize - pagination.pageSize;
    payload.limit = pagination.pageSize;
    dispatch(orgUserListLoad({ ...payload, department }));
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "key",
      render: (text, record) => `${record._id}`,
    },
    {
      title: "Name",
      dataIndex: "userName",
      render: (text, record) => `${record?.userName || "N/A"}`,
    },
    {
      title: "Department",
      dataIndex: "department",
      render: (text, record) => `${record?.department?.name || "N/A"}`,
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Action",
      dataIndex: "action",
      align: "center",
      render: (text, record) => (
        <div className="flex flex-row justify-evenly">
          <Popconfirm
            title="Are you sure to delete this user?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
          >
            <div>
              <img src={images.trash} alt="trash" />
            </div>
          </Popconfirm>

          <div>
            <img
              src={images.edit}
              alt="trash"
              className="h-5 w-5"
              onClick={() => {
                setEditMode(true);
                setRecord(record);
                setModalFun(setEditMode);
              }}
            />
          </div>
        </div>
      ),
    },
  ];

  // P A G I N A T I O N

  // --S T A T E--
  const START = useMemo(() => OFFSET + 1, [userList.userList]);
  const TOTAL_LIST_ITEMS = useMemo(
    () => userList.pagination?.totalCount,
    [userList.userList]
  );
  const END = useMemo(
    () => Math.min(OFFSET + LIST_LIMIT, TOTAL_LIST_ITEMS),
    [userList.userList]
  );
  const canCallNextData = OFFSET + LIST_LIMIT < TOTAL_LIST_ITEMS;
  // --A C T I O N S--
  const paginationHandler = useCallback(
    (type) => {
      if (type === "pre") {
        OFFSET = OFFSET - LIST_LIMIT;
        const data = {
          offset: OFFSET,
          limit: LIST_LIMIT,
          search: search,
          sort: "createdAt",
          organisation: myid,
          order: -1,
        };
        dispatch(orgUserListLoad({ ...data, department }));
      } else {
        OFFSET = OFFSET + LIST_LIMIT;
        const data = {
          offset: OFFSET,
          limit: LIST_LIMIT,
          search: search,
          organisation: myid,
          order: -1,
        };
        dispatch(orgUserListLoad({ ...data, department }));
      }
    },
    [userList.userList]
  );

  const numberOfRowHandler = useCallback(
    (ddateStringate, dataObj) => {
      LIST_LIMIT = dataObj.label;
      OFFSET = 0;
      const data = {
        offset: OFFSET,
        limit: LIST_LIMIT,
        search: search,
        organisation: myid,
        order: -1,
      };
      dispatch(orgUserListLoad({ ...data, department }));
    },
    [userList.userList]
  );

  const searchHandler = useCallback(
    (e) => {
      if (emptySpaceInputHandler(e) || e.target.value.length == "") {
        LIST_LIMIT = 10;
        OFFSET = 0;
        setSearch(emptySpaceInputHandler(e));
        const data = {
          offset: 0,
          limit: 10,
          search: emptySpaceInputHandler(e).trim(),
          organisation: myid,
          order: -1,
        };
        debounce(() => dispatch(orgUserListLoad(data)));
      }
    },
    [userList.userList]
  );

  const userExportHandler = () =>
    dispatch(
      orgUserExportListLoad({
        organisation: myid,
        limit: -1,
        department,
      })
    );

  return (
    <div className="content-e outter-space">
      <div className="user-heading">
        <h1>User</h1>
        <div className="flex">
          <div className="flex gap-4">
            <button
              style={{ width: 150, height: 35, borderRadius: "20px" }}
              onClick={userExportHandler}
            >
              Export Users
            </button>
            <button
              style={{ width: 180, height: 35, borderRadius: "20px" }}
              onClick={() => setModal2Open(true)}
            >
              Add New Users
            </button>
          </div>
          <div className="!w-[150px] ml-3 select-edit">
            <Select
              onChange={(val) => setDepartment(val)}
              value={department}
              placeholder={"Department"}
              options={departMentOptions}
              className="w-full"
            />
          </div>
        </div>
      </div>
      <div className="bg-gradient-primary remove-radius system-line">
        <input
          type="text"
          className="iocn-search search-bar"
          placeholder="Search User"
          value={search}
          onChange={searchHandler}
        />
      </div>
      <Table
        columns={columns}
        dataSource={userList.userList?.length > 0 ? userList.userList : []}
        pagination={{
          current: 0,
          pageSize: 0,
          total: TOTAL_LIST_ITEMS,
        }}
        loading={isLoading}
        onChange={onChange}
      />
      {TOTAL_LIST_ITEMS > 6 && (
        <div className="footer-section">
          <div className="foot-main-div">
            <div className="rows">
              <div className="row-per-page">
                <p>Rows per page</p>
              </div>
              <div className="top-checkbox fot-select">
                <Select
                  defaultValue={LIST_LIMIT}
                  onChange={numberOfRowHandler}
                  options={numberOfRowOptionList}
                />
              </div>
            </div>

            <div className="page-count">
              <p>{`${START}-${END} of ${TOTAL_LIST_ITEMS}`}</p>
            </div>

            <div className="back-and-next">
              {/* <span><img src={images.back} alt="back" /></span> */}
              <span onClick={() => OFFSET !== 0 && paginationHandler("pre")}>
                <img
                  src={OFFSET == 0 ? images.per : images.paginationDimLeft}
                  style={{ tintColor: "red" }}
                  alt="back"
                />
              </span>
              <span
                onClick={() => canCallNextData && paginationHandler("next")}
              >
                <img
                  src={
                    canCallNextData == false
                      ? images.paginationDimRight
                      : images.new
                  }
                  alt="back"
                />
              </span>
              {/* <span><img src={images.next} alt="back" /></span> */}
            </div>
          </div>
        </div>
      )}

      <Modal
        title=""
        className="modal-update"
        centered
        open={modal2Open}
        closable={true}
        onCancel={() => setModal2Open(false)}
        footer={false}
        width={600}
      >
        <div className="add-user-popup">
          <div className="modal-add-user">
            <h1>Add Users</h1>
          </div>
          <div className="click-to-upload">
            <div className="upload-file">
              <img src={images.fileImg} alt="back" />
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
                accept=".csv,.xls, application/vnd.ms-excel"
                onChange={handleFileChange}
              />
              <div>
                <button onClick={handleClick}>Click here to upload</button>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal
        title={<div className="text-3xl text-dark mt-6">Assign Department</div>}
        visible={editMode}
        onOk={() => {}}
        onCancel={() => {
          setEditMode(false);
          setRecord(null);
        }}
        footer={null}
        centered
        className="departmentModal"
        destroyOnClose={true}
      >
        <form onSubmit={formik.handleSubmit}>
          <div>
            <div className="orgn-plans">
              <div className="modal-right-side">
                <div className="form-group">
                  <label>User Name</label>
                  <input
                    className="form-control"
                    type="text"
                    placeholder="Enter department name"
                    name="name"
                    value={record?.userName || "N/A"}
                    disabled={true}
                  />
                </div>
                <div className="form-group">
                  <label>Department Name</label>
                  <div className="!w-[150px] select-edit">
                    <Select
                      onChange={(val) => {
                        formik.setFieldValue("department", val);
                      }}
                      value={formik.values.department}
                      placeholder={"Department"}
                      options={departMentOptions?.filter(
                        (item) => item.label !== "All"
                      )}
                      className="w-full"
                      onBlur={formik.handleBlur}
                    />
                  </div>
                  {formik.errors.department && formik.touched.department ? (
                    <div className="text-red-500 text-xs mt-1 font-normal">
                      {formik.errors.department}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          <div className="click-to-upload top-border-line">
            <div className="create-inner-circle-btn button-spacing">
              <button type="submit" onClick={formik.handleSubmit}>
                Assign Department
              </button>
            </div>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default UserTable;
